(() => {
  if (customElements.get('em-mobile-buy-bar')) return;
  customElements.define('em-mobile-buy-bar', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.section = document.querySelector(`[data-em-section="${this.dataset.section}"]`);
      this.form = this.section?.querySelector('product-form form');
      this.original = this.form?.querySelector('[name="add"][type="submit"]');
      this.picker = this.section?.querySelector('variant-radios, variant-selects');
      if (!this.form || !this.original || !this.picker) return;
      this.ready = true;
      this.variants = JSON.parse(this.picker.querySelector('[type="application/json"]').textContent);
      this.add = this.querySelector('[data-buy-bar-add]');
      this.selection = this.querySelector('[data-buy-bar-selection]');
      this.price = this.querySelector('[data-buy-bar-price]');
      this.mobile = matchMedia('(max-width: 749px)');
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
      };
      this.add.addEventListener('click', () => this.submit(), options);
      this.querySelector('[data-buy-bar-options]').addEventListener('click', event => {
        event.preventDefault();
        this.goToOptions();
      }, options);
      this.form.addEventListener('submit', event => {
        if (this.pending && !this.forwarding) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, {...options, capture: true});
      this.form.addEventListener('em:buy-bar-complete', () => {
        this.pending = false;
        const error = this.section.querySelector('.product-form__error-message-wrapper:not([hidden])');
        if (error) {
          error.setAttribute('tabindex', '-1');
          error.scrollIntoView({block: 'center'});
          error.focus({preventScroll: true});
        }
        this.schedule();
      }, options);
      window.addEventListener('pageshow', () => { this.pending = false; this.schedule(); }, options);
      window.addEventListener('scroll', this.schedule, {...options, passive: true});
      window.addEventListener('resize', this.schedule, options);
      window.visualViewport?.addEventListener('resize', this.schedule, options);
      window.visualViewport?.addEventListener('scroll', this.schedule, options);
      this.mobile.addEventListener('change', this.schedule, options);
      document.addEventListener('focusin', this.schedule, options);
      document.addEventListener('focusout', this.schedule, options);
      document.addEventListener('change', this.schedule, options);
      document.addEventListener('click', this.schedule, options);
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target) && !(record.target === document.body && record.attributeName === 'style'))) this.schedule();
      });
      this.observer.observe(document.body, {childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden', 'aria-disabled', 'disabled', 'value']});
      this.sizeObserver = new ResizeObserver(this.schedule);
      this.sizeObserver.observe(this);
      this.style.setProperty('--em-buy-bar-accent', getComputedStyle(this.section).getPropertyValue('--em-burgundy').trim() || '#960c0c');
      this.update();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.sizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      document.body.classList.remove('em-buy-bar-active');
      document.body.style.removeProperty('--em-buy-bar-height');
      this.ready = false;
    }
    shown(element) {
      if (!element || element.closest('[hidden], [aria-hidden="true"]')) return false;
      const style = getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0 && rect.width > 0 && rect.height > 0;
    }
    options() {
      return this.picker.matches('variant-radios')
        ? [...this.picker.querySelectorAll('fieldset')].map(fieldset => fieldset.querySelector('input:checked')?.value || '')
        : [...this.picker.querySelectorAll('select')].map(select => select.value);
    }
    variant() {
      const selected = this.options();
      return this.variants.find(variant => variant.options.length === selected.length && variant.options.every((value, index) => value === selected[index]));
    }
    blocked() {
      const active = document.activeElement;
      if (active?.matches('textarea, select, [contenteditable="true"], input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"]):not([type="submit"]):not([type="button"])')) return true;
      if (window.visualViewport && window.innerHeight - window.visualViewport.height > 120) return true;
      if (document.body.matches('.cart-drawer-open, .overflow-hidden, .overflow-hidden-mobile, .overflow-hidden-tablet')) return true;
      return [...document.querySelectorAll('dialog[open], modal-dialog[open], product-modal[open], [role="dialog"], [aria-modal="true"], #email-me-frame')].some(element => {
        const rect = element.getBoundingClientRect();
        return this.shown(element) && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < document.documentElement.clientWidth;
      });
    }
    setText(element, value) {
      if (element.textContent !== value) element.textContent = value;
    }
    update() {
      if (!this.original.isConnected || !this.picker.isConnected) { this.hidden = true; return; }
      const variant = this.variant();
      const originalBusy = this.original.classList.contains('loading') || this.original.getAttribute('aria-disabled') === 'true';
      const matchesForm = variant && String(variant.id) === this.form.elements.namedItem('id')?.value;
      const available = matchesForm && variant.available && !this.original.disabled;
      const quantity = this.form.elements.namedItem('quantity');
      const quantityValid = !quantity || (quantity.checkValidity() && Number(quantity.value) > 0);
      this.add.disabled = !available || !quantityValid || this.pending || originalBusy;
      const buttonText = this.original.querySelector('span')?.textContent.trim() || this.original.textContent.trim();
      this.setText(this.add, this.pending || originalBusy ? this.dataset.adding : buttonText);
      const selection = this.options().join(' · ') + (quantity && Number(quantity.value) > 1 ? ` × ${quantity.value}` : '');
      this.setText(this.selection, selection);
      const priceRoot = this.section.querySelector('.em-product-price');
      const price = [...(priceRoot?.querySelectorAll('.price__sale .price-item--sale, .price__regular .price-item--regular') || [])].find(element => this.shown(element));
      this.setText(this.price, matchesForm && price && (!this.original.disabled || !variant.available) ? price.textContent.trim() : '');
      const above = this.original.getBoundingClientRect().bottom <= 0;
      const visible = this.mobile.matches && above && this.shown(this.original) && !this.blocked();
      this.hidden = !visible;
      document.body.classList.toggle('em-buy-bar-active', visible);
      if (visible) {
        const height = `${Math.ceil(this.getBoundingClientRect().height)}px`;
        if (document.body.style.getPropertyValue('--em-buy-bar-height') !== height) document.body.style.setProperty('--em-buy-bar-height', height);
      }
    }
    goToOptions() {
      const target = this.picker.querySelector('input:checked, select, input');
      this.picker.style.scrollMarginTop = '110px';
      this.picker.scrollIntoView({block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      target?.focus({preventScroll: true});
    }
    submit() {
      this.update();
      if (this.add.disabled || this.hidden) return;
      if (!this.form.checkValidity()) {
        const invalid = [...this.form.elements].find(element => element.willValidate && !element.validity.valid);
        invalid?.scrollIntoView({block: 'center'});
        this.form.reportValidity();
        return;
      }
      this.pending = true;
      this.form.dataset.emBuyBarSubmission = 'true';
      this.form.dataset.emBuyBarError = this.dataset.error;
      this.forwarding = true;
      this.original.click();
      this.forwarding = false;
      this.schedule();
    }
  });
})();
